import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Search, HelpCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { API } from "@/App";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

const faqCategories = [
  { id: "general", label: "General" },
  { id: "storage", label: "Storage" },
  { id: "nutrition", label: "Nutrition" },
  { id: "growing", label: "Growing" },
  { id: "delivery", label: "Delivery" },
  { id: "subscription", label: "Subscription" }
];

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [filteredFaqs, setFilteredFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await axios.get(`${API}/faq`);
        setFaqs(response.data);
        setFilteredFaqs(response.data);
      } catch (error) {
        console.error("Failed to fetch FAQs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFaqs();
  }, []);

  useEffect(() => {
    let filtered = faqs;

    if (selectedCategory !== "all") {
      filtered = filtered.filter(faq => faq.category === selectedCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        faq =>
          faq.question.toLowerCase().includes(query) ||
          faq.answer.toLowerCase().includes(query)
      );
    }

    setFilteredFaqs(filtered);
  }, [searchQuery, selectedCategory, faqs]);

  return (
    <div className="pt-16 md:pt-20 lg:pt-24" data-testid="faq-page">
      <SEO title="FAQ" description="Find answers to frequently asked questions about microgreens, ordering, delivery, and subscriptions at Praroha Greens." keywords="microgreens faq, delivery questions, subscription" path="/faq" />
      {/* Hero */}
      <section className="py-8 md:py-10 bg-praroha-forest" data-testid="faq-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center gap-2 mb-2">
                <HelpCircle className="w-5 h-5 text-praroha-sage" />
                <span className="text-praroha-sage text-sm font-medium">Help Center</span>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
                Frequently Asked Questions
              </h1>
              <p className="text-white/50 text-sm mt-1">
                Find answers about our microgreens, storage, delivery, and more.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="w-full md:w-auto"
            >
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  type="text"
                  placeholder="Search for answers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 w-full sm:w-72 focus:bg-white/15"
                  data-testid="faq-search-input"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Category Pills */}
      <section className="py-6 border-b border-border/50 sticky top-16 md:top-20 bg-white/95 backdrop-blur-md z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            <Button
              variant={selectedCategory === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory("all")}
              className={`rounded-full ${
                selectedCategory === "all"
                  ? "bg-praroha-forest text-white"
                  : "border-praroha-forest/30 text-praroha-forest hover:bg-praroha-forest/5"
              }`}
              data-testid="faq-category-all"
            >
              All
            </Button>
            {faqCategories.map((cat) => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(cat.id)}
                className={`rounded-full ${
                  selectedCategory === cat.id
                    ? "bg-praroha-forest text-white"
                    : "border-praroha-forest/30 text-praroha-forest hover:bg-praroha-forest/5"
                }`}
                data-testid={`faq-category-${cat.id}`}
              >
                {cat.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-12 md:py-16 bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="space-y-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-16 bg-muted animate-pulse rounded-xl" />
              ))}
            </div>
          ) : filteredFaqs.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground text-lg">No FAQs found matching your search.</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("all");
                }}
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {filteredFaqs.map((faq, index) => (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <AccordionItem
                    value={faq.id}
                    className="bg-white rounded-xl border border-border/50 px-6 data-[state=open]:shadow-soft"
                    data-testid={`faq-item-${index}`}
                  >
                    <AccordionTrigger className="text-left py-5 hover:no-underline">
                      <span className="font-heading text-lg font-medium text-praroha-forest pr-4">
                        {faq.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-5">
                      <p className="text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          )}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 bg-praroha-forest text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
            Still Have Questions?
          </h2>
          <p className="text-white/80 mb-8">
            Can't find the answer you're looking for? Our team is here to help.
          </p>
          <Link to="/contact" data-testid="contact-us-btn">
            <Button className="bg-praroha-terracotta hover:bg-praroha-terracotta/90 text-white rounded-full px-8 py-6 text-lg">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
